import { useAuth } from "@contexts/auth";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Badge,
  Divider,
  Grid,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Timeline,
  Title,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import dayjs from "dayjs";

import { OnboardingInvitationAccess } from "@components/OnboardingInvitationAccess";

import {
  useProspect,
  useProspectInvitationAccess,
} from "@hooks/queries/accountManagement/useAccountManagement";

import { Prospect } from "@customTypes/accountManagement";

import { ProspectReferralAssignment } from "./ProspectReferralAssignment";


interface Props {
  opened: boolean;
  onClose: () => void;
  prospect?: Prospect | null;
}

const Field = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {value || "—"}
    </Text>
  </div>
);

const invitationDisabledMessages: Record<string, string> = {
  expired: "This invitation has expired. Renew it before sharing.",
  completed: "This invitation has already been completed.",
  rejected: "This invitation belongs to a rejected onboarding session.",
  not_sent: "This invitation is not available for sharing.",
};

export const ProspectDetailsModal = ({
  opened,
  onClose,
  prospect: initialProspect,
}: Props) => {
  const { t } = useTranslation();
  const { can } = useAuth();
  const details = useProspect(initialProspect?._id || "", opened);
  const prospect = details.data || initialProspect;
  const invitation = useProspectInvitationAccess(
    prospect?._id || "",
    opened && Boolean(prospect?.onboarding_session_id) && can("users.invite") && (prospect?.account_type === "doctor" || can("access.manage"))
  );
  const copyEnabled = ["active", "in_progress"].includes(
    invitation.data?.invitation_status || ""
  );
  const disabledMessage =
    invitationDisabledMessages[invitation.data?.invitation_status || ""];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Prospect Details"
      size="xl"
      centered>
      {prospect && (
      <Grid>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Group mb="lg">
            <Avatar size={52} color="violet">
              {(prospect.name || prospect.account_type)
                .split(" ")
                .map(value => value[0])
                .join("")
                .slice(0, 2)}
            </Avatar>
            <div>
              <Title order={4}>{prospect.name || "—"}</Title>
              <Group gap="xs">
                <Badge variant="light">{prospect.account_type}</Badge>
                <Badge variant="light" color="violet">
                  {prospect.status.replaceAll("_", " ")}
                </Badge>
              </Group>
            </div>
          </Group>
          <Grid mb="lg">
            <Grid.Col span={6}>
              <Field label="Phone number" value={prospect.phone_number} />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field label="WhatsApp" value={prospect.whatsapp_number} />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field label="Email" value={prospect.email} />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field label="Social media" value={prospect.social_handle} />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field
                label="Specialty / department"
                value={prospect.specialty_or_department}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field label="Lead source" value={prospect.lead_source} />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field
                label="Invitation"
                value={prospect.invitation_status.replaceAll("_", " ")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field
                label="Invitation expires"
                value={
                  prospect.invitation_expires_at
                    ? dayjs(prospect.invitation_expires_at).format(
                        "D MMM YYYY, h:mm A"
                      )
                    : undefined
                }
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field
                label="Identified by"
                value={prospect.identified_by_name}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Field
                label="Identified on"
                value={dayjs(prospect.identified_at).format("D MMM YYYY")}
              />
            </Grid.Col>
          </Grid>
          <Divider my="md" />
          <Text size="xs" c="dimmed" mb={6}>
            Qualification tags
          </Text>
          <Group gap="xs" mb="lg">
            {prospect.qualification_tags.length ? (
              prospect.qualification_tags.map(tag => (
                <Badge key={tag} variant="light" color="violet">
                  {tag}
                </Badge>
              ))
            ) : (
              <Text size="sm">—</Text>
            )}
          </Group>
          <Paper p="sm" bg="gray.0">
            <Text size="xs" c="dimmed" mb={4}>
              Notes
            </Text>
            <Text size="sm">{prospect.notes || "No notes added."}</Text>
          </Paper>
          {prospect.public_suggestion_count > 0 && (
            <>
              <Divider my="md" />
              <Title order={5} mb="sm">
                Public suggestion context
              </Title>
              <Paper p="sm" bg="teal.0">
                <Grid>
                  <Grid.Col span={6}>
                    <Field
                      label="Suggestions received"
                      value={String(prospect.public_suggestion_count)}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field
                      label="Latest suggestion"
                      value={
                        prospect.public_suggestion_latest_at
                          ? dayjs(prospect.public_suggestion_latest_at).format(
                              "D MMM YYYY, h:mm A"
                            )
                          : undefined
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field
                      label="Search query"
                      value={prospect.public_suggestion_search_query}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field
                      label="Relationship"
                      value={prospect.public_suggestion_relationship}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field
                      label="Submitted by"
                      value={prospect.public_suggestion_submitter_name}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field
                      label="Submitter contact"
                      value={prospect.public_suggestion_submitter_contact}
                    />
                  </Grid.Col>
                </Grid>
                <Text size="xs" c="dimmed" mt="sm">
                  Public suggestions are not referral-reward eligible and do
                  not send an invitation automatically.
                </Text>
              </Paper>
            </>
          )}
          {prospect.referrer_name && (
            <>
              <Divider my="md" />
              <Title order={5} mb="sm">
                Referral attribution
              </Title>
              <Paper p="sm" bg="violet.0">
                <Grid>
                  <Grid.Col span={6}>
                    <Field label="Referrer" value={prospect.referrer_name} />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field label="Referrer type" value={prospect.referrer_type} />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field
                      label="Compensation plan"
                      value={
                        prospect.referral_plan_name
                          ? `${prospect.referral_plan_name} · v${prospect.referral_plan_version}`
                          : undefined
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Field
                      label={prospect.reward_amount_status === "estimated" ? "Estimated reward" : "Confirmed reward"}
                      value={
                        prospect.promised_reward_cfa != null
                          ? `${prospect.promised_reward_cfa.toLocaleString()} CFA`
                          : undefined
                      }
                    />
                  </Grid.Col>
                </Grid>
                {prospect.referral_attribution_history?.length > 0 && (
                  <Stack gap="xs" mt="md">
                    <Text size="xs" c="dimmed">
                      Attribution history
                    </Text>
                    {prospect.referral_attribution_history.map((item, index) => (
                      <div key={`${item.occurred_at}-${index}`}>
                        <Text size="sm">
                          {item.previous_referrer_name ? `${item.previous_referrer_name} → ` : `${t("referralAssignment.assignedTo")} `}
                          {item.new_referrer_name || "New referrer"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dayjs(item.occurred_at).format("D MMM YYYY, h:mm A")}
                          {item.reason ? ` · ${item.reason}` : ""}
                        </Text>
                      </div>
                    ))}
                  </Stack>
                )}
              </Paper>
            </>
          )}
          <Divider my="md" />
          {opened && can("access.manage") && <ProspectReferralAssignment prospectId={prospect._id} />}
          <Title order={5} mb="sm" mt="md">
            Invitation access
          </Title>
          {!prospect.onboarding_session_id ? (
            <Paper p="sm" bg="gray.0">
              <Text size="sm" c="dimmed">
                No invitation created.
              </Text>
            </Paper>
          ) : invitation.isLoading ? (
            <Text size="sm" c="dimmed">
              Loading invitation access…
            </Text>
          ) : invitation.isError || !invitation.data ? (
            <Text size="sm" c="red">
              Invitation access details could not be loaded.
            </Text>
          ) : (
            <OnboardingInvitationAccess
              invitation={invitation.data}
              copyEnabled={copyEnabled}
              disabledMessage={disabledMessage}
            />
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Title order={5} mb="md">
            Onboarding timeline
          </Title>
          <Timeline
            active={prospect.stage_history.length}
            bulletSize={24}
            lineWidth={2}>
            {prospect.stage_history.map((transition, index) => (
              <Timeline.Item
                key={`${transition.status}-${index}`}
                bullet={<IconCheck size={14} />}
                title={transition.status.replaceAll("_", " ")}>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    {dayjs(transition.occurred_at).format(
                      "D MMM YYYY, h:mm A"
                    )}
                  </Text>
                  {(transition.actor_name || transition.note) && (
                    <Text size="xs">
                      {[transition.actor_name, transition.note]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  )}
                </Stack>
              </Timeline.Item>
            ))}
          </Timeline>
        </Grid.Col>
      </Grid>
      )}
    </Modal>
  );
};
